import { useNavigate } from 'react-router-dom';
 
// ✅ Constants
const COMPANY_NAME = 'AastroG';
const CONTACT_EMAIL = 'aastrogai@gmail.com';
const CONTACT_PHONE = '+91 88668 23366';
const EFFECTIVE_DATE = 'November 10, 2025';
 
// ✅ Reusable Components
const SectionTitle = ({ children }) => <h2 className="about-section-title">{children}</h2>;
const SubSectionTitle = ({ children }) => <h3 className="about-subsection-title">{children}</h3>;
const TextBlock = ({ children }) => <p className="about-text">{children}</p>;
const List = ({ items }) => (
  <ul className="about-list">
    {items.map((item, index) => (
      <li key={index} className="about-list-item">{item}</li>
    ))}
  </ul>
);
 
function Policy() {
  const navigate = useNavigate();
 
  return (
    <div className="about-container">
      <h1 className="heading">Privacy Policy</h1>
     
      <TextBlock>
        <strong>Effective Date:</strong> {EFFECTIVE_DATE}<br />
        <strong>Last Updated:</strong> {EFFECTIVE_DATE}
      </TextBlock>
 
      <SectionTitle>Introduction</SectionTitle>
      <TextBlock>
        Welcome to <strong>{COMPANY_NAME}</strong> ("we," "us," "our," or "Company"). We are committed to
        protecting your privacy and ensuring the security of your personal information. This Privacy Policy
        explains how we collect, use, store, share, and protect your data when you use our website, mobile
        applications, and services (collectively, the "Service").
      </TextBlock>
      <TextBlock>
        By accessing or using {COMPANY_NAME}, you agree to the terms of this Privacy Policy. If you do not
        agree, please discontinue use of our Service immediately.
      </TextBlock>
      <TextBlock>
        <strong>Contact Information:</strong><br />
        {COMPANY_NAME}<br />
        Email: {CONTACT_EMAIL}<br />
        Phone: {CONTACT_PHONE}<br />
        WhatsApp: {CONTACT_PHONE}
      </TextBlock>
 
      <SectionTitle>1. Information We Collect</SectionTitle>
      <TextBlock>We collect different types of information to provide and improve our Service:</TextBlock>
 
      <SubSectionTitle>1.1 Personal Information You Provide</SubSectionTitle>
      <TextBlock><strong>Account Information:</strong></TextBlock>
      <List
        items={[
          'Full name',
          'Email address',
          'Phone number (optional)',
          'Date of birth, time of birth, and place of birth (for astrological calculations)',
          'Gender',
          'Profile picture (optional)',
        ]}
      />
      <TextBlock><strong>Authentication Data:</strong></TextBlock>
      <List
        items={[
          'Google Sign-In credentials (email, name, profile photo)',
          'Password (encrypted and securely stored)',
        ]}
      />
      <TextBlock><strong>Payment Information:</strong></TextBlock>
      <List
        items={[
          'Transaction history',
          'Credit/wallet balance',
          'Payment method details (processed securely through third-party payment gateways; we do NOT store credit card numbers)',
        ]}
      />
      <TextBlock><strong>Communication Data:</strong></TextBlock>
      <List
        items={[
          'Chat messages with our AI astrology assistant',
          'Consultation history with astrologers',
          'Feedback and support inquiries',
          'User-generated content (questions, reviews, testimonials)',
        ]}
      />
 
      <SubSectionTitle>1.2 Automatically Collected Information</SubSectionTitle>
      <TextBlock><strong>Device Information:</strong></TextBlock>
      <List
        items={[
          'Device type, model, and operating system',
          'Unique device identifiers (Android ID, Advertising ID)',
          'Mobile network information',
          'IP address',
          'Browser type and version',
        ]}
      />
      <TextBlock><strong>Usage Data:</strong></TextBlock>
      <List
        items={[
          'App features used',
          'Pages visited and time spent',
          'Session duration and frequency',
          'Click patterns and navigation paths',
          'Search queries within the app',
        ]}
      />
      <TextBlock><strong>Location Data:</strong></TextBlock>
      <List
        items={[
          'Approximate location (city, state, country) derived from IP address',
          'Precise location (with your explicit permission) for birth chart accuracy',
        ]}
      />
 
      <SubSectionTitle>1.3 Third-Party Data</SubSectionTitle>
      <TextBlock><strong>Social Media:</strong></TextBlock>
      <List
        items={[
          'If you sign in using Google, we receive basic profile information (name, email, profile photo) as permitted by Google',
        ]}
      />
      <TextBlock><strong>Analytics Services:</strong></TextBlock>
      <List
        items={[
          'We use Firebase Analytics, Google Analytics, and similar services to understand app performance and user behavior',
        ]}
      />
 
      <SectionTitle>2. How We Use Your Information</SectionTitle>
      <TextBlock>We use the collected information for the following purposes:</TextBlock>
 
      <SubSectionTitle>2.1 Core Service Delivery</SubSectionTitle>
      <List
        items={[
          'Generate personalized birth charts (Kundli/Janam Patri)',
          'Provide AI-powered astrological chat responses',
          'Connect you with expert astrologers for consultations',
          'Calculate horoscopes, compatibility, and astrological predictions',
          'Send daily/weekly/monthly horoscope notifications (if enabled)',
        ]}
      />
 
      <SubSectionTitle>2.2 Account Management</SubSectionTitle>
      <List
        items={[
          'Create and manage your account',
          'Authenticate your identity (Google Sign-In)',
          'Process payments and maintain credit balance',
          'Enable chat and call features with astrologers',
        ]}
      />
 
      <SubSectionTitle>2.3 Service Improvement</SubSectionTitle>
      <List
        items={[
          'Analyze usage patterns to improve AI accuracy',
          'Develop new features based on user feedback',
          'Conduct A/B testing and product research',
          'Fix bugs and technical issues',
        ]}
      />
 
      <SubSectionTitle>2.4 Communication</SubSectionTitle>
      <List
        items={[
          'Send transactional emails (account verification, password reset)',
          'Provide customer support',
          'Send promotional offers and updates (with your consent)',
          'Request feedback and reviews',
        ]}
      />
 
      <SubSectionTitle>2.5 Safety and Security</SubSectionTitle>
      <List
        items={[
          'Detect and prevent fraud, spam, and abuse',
          'Enforce our Terms and Conditions',
          'Comply with legal obligations',
          'Protect user rights and safety',
        ]}
      />
 
      <SubSectionTitle>2.6 Legal Compliance</SubSectionTitle>
      <List
        items={[
          'Respond to law enforcement requests',
          'Comply with court orders and legal processes',
          'Protect our rights, property, and safety',
        ]}
      />
 
      <SectionTitle>3. How We Share Your Information</SectionTitle>
      <TextBlock>
        We respect your privacy and do NOT sell your personal information to third parties. However, we
        may share your data in the following limited circumstances:
      </TextBlock>
 
      <SubSectionTitle>3.1 With Astrologers</SubSectionTitle>
      <TextBlock>When you book a consultation, we share necessary information with the assigned astrologer:</TextBlock>
      <List
        items={[
          'Your name, birth details (date, time, place)',
          'Your question or consultation request',
          'Chat/call history for that specific consultation',
        ]}
      />
      <TextBlock>
        <strong>Note:</strong> Astrologers are bound by confidentiality agreements and cannot misuse your data.
      </TextBlock>
 
      <SubSectionTitle>3.2 Service Providers</SubSectionTitle>
      <TextBlock>We work with trusted third-party service providers who help us operate our Service:</TextBlock>
      <List
        items={[
          <><strong>Firebase (Google Cloud):</strong> App hosting, authentication, database, analytics</>,
          <><strong>Azure Blob Storage:</strong> File storage and backup</>,
          <><strong>Payment Gateways:</strong> Razorpay, Stripe, PayPal (for processing payments)</>,
          <><strong>Communication Services:</strong> Twilio, Agora (for chat and call features)</>,
          <><strong>Email Services:</strong> SendGrid, Amazon SES (for transactional emails)</>,
          <><strong>Analytics:</strong> Google Analytics, Firebase Analytics</>,
        ]}
      />
      <TextBlock>
        These providers have access to your data only to perform tasks on our behalf and are obligated
        to protect it.
      </TextBlock>
 
      <SubSectionTitle>3.3 Legal Requirements</SubSectionTitle>
      <TextBlock>We may disclose your information if required by law:</TextBlock>
      <List
        items={[
          'In response to subpoenas, court orders, or legal processes',
          'To protect our rights, property, or safety',
          'To investigate fraud, security issues, or violations of our Terms',
          'To comply with government agencies or regulatory authorities',
        ]}
      />
 
      <SubSectionTitle>3.4 Business Transfers</SubSectionTitle>
      <TextBlock>
        If {COMPANY_NAME} is acquired, merged, or sells assets, your information may be transferred to the
        new owner. We will notify you via email or prominent notice in the app.
      </TextBlock>
 
      <SubSectionTitle>3.5 With Your Consent</SubSectionTitle>
      <TextBlock>
        We may share your information with other parties when you explicitly consent (e.g., sharing a
        consultation report with family members).
      </TextBlock>
 
      <SectionTitle>4. Data Security</SectionTitle>
      <TextBlock>We take your security seriously and implement industry-standard measures to protect your data:</TextBlock>
 
      <SubSectionTitle>4.1 Technical Measures</SubSectionTitle>
      <List
        items={[
          <>
            <strong>Encryption in Transit:</strong> All data transmitted between your device and our servers
            uses HTTPS/SSL/TLS encryption
          </>,
          <>
            <strong>Encryption at Rest:</strong> Sensitive data (passwords, payment info) is encrypted in our
            databases
          </>,
          <><strong>Secure Authentication:</strong> Google Sign-In uses OAuth 2.0 protocol</>,
          <><strong>Access Controls:</strong> Only authorized personnel can access user data</>,
          <><strong>Regular Backups:</strong> Your data is backed up regularly to prevent loss</>,
        ]}
      />
 
      <SubSectionTitle>4.2 Organizational Measures</SubSectionTitle>
      <List
        items={[
          'Employee training on data protection',
          'Non-disclosure agreements with team members and partners',
          'Regular security audits and vulnerability assessments',
          'Incident response procedures',
        ]}
      />
      <TextBlock>
        <strong>Limitation:</strong> While we strive to protect your data, no method of transmission or
        storage is 100% secure. We cannot guarantee absolute security.
      </TextBlock>
 
      <SectionTitle>5. Your Rights and Choices</SectionTitle>
      <TextBlock>You have control over your personal information:</TextBlock>
 
      <SubSectionTitle>5.1 Access and Update</SubSectionTitle>
      <List
        items={[
          'View and edit your profile information in the app settings',
          `Request a copy of your data by emailing ${CONTACT_EMAIL}`,
        ]}
      />
 
      <SubSectionTitle>5.2 Delete Your Data</SubSectionTitle>
      <List
        items={[
          'Delete your account directly from app settings',
          'Request data deletion by contacting us (we will comply within 30 days)',
          'Note: Some data may be retained for legal or operational purposes',
        ]}
      />
 
      <SubSectionTitle>5.3 Control Communications</SubSectionTitle>
      <List
        items={[
          'Unsubscribe from promotional emails using the link in each email',
          'Disable push notifications in your device settings',
          'Manage notification preferences in app settings',
        ]}
      />
 
      <SubSectionTitle>5.4 Revoke Permissions</SubSectionTitle>
      <List
        items={[
          'Revoke location access in your device settings',
          'Disconnect Google Sign-In from your Google Account settings',
          'Withdraw consent for data processing (may limit app functionality)',
        ]}
      />
 
      <SubSectionTitle>5.5 GDPR Rights (for EU/EEA Users)</SubSectionTitle>
      <TextBlock>
        If you're in the European Union or European Economic Area, you have additional rights:
      </TextBlock>
      <List
        items={[
          <><strong>Right to Access:</strong> Request what data we hold about you</>,
          <><strong>Right to Rectification:</strong> Correct inaccurate data</>,
          <><strong>Right to Erasure:</strong> Request deletion ("right to be forgotten")</>,
          <><strong>Right to Restriction:</strong> Limit how we process your data</>,
          <><strong>Right to Data Portability:</strong> Receive your data in a structured format</>,
          <><strong>Right to Object:</strong> Object to certain processing activities</>,
          <><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</>,
        ]}
      />
      <TextBlock>To exercise these rights, email us at {CONTACT_EMAIL}</TextBlock>
 
      <SectionTitle>6. Data Retention</SectionTitle>
      <TextBlock>
        We retain your information for as long as necessary to provide our Service and comply with legal
        obligations:
      </TextBlock>
      <List
        items={[
          <><strong>Active Accounts:</strong> Data retained while your account is active</>,
          <>
            <strong>Deleted Accounts:</strong> Most data deleted within 30 days; some retained for legal
            compliance (up to 7 years)
          </>,
          <><strong>Consultation Records:</strong> Retained for 2 years for quality assurance</>,
          <><strong>Payment Records:</strong> Retained for 7 years for tax and accounting purposes</>,
          <><strong>Analytics Data:</strong> Anonymized and retained indefinitely</>,
        ]}
      />
 
      <SectionTitle>7. Children's Privacy</SectionTitle>
      <TextBlock>
        {COMPANY_NAME} is NOT intended for users under 18 years of age. We do not knowingly collect data
        from children.
      </TextBlock>
      <TextBlock>If you are under 18:</TextBlock>
      <List
        items={[
          'You must have parental consent to use our Service',
          'Parents/guardians are responsible for monitoring usage',
        ]}
      />
      <TextBlock>
        If we discover we have collected data from a child without parental consent, we will delete it
        immediately. Contact us at {CONTACT_EMAIL} if you believe we have such data.
      </TextBlock>
 
      <SectionTitle>8. International Data Transfers</SectionTitle>
      <TextBlock>
        {COMPANY_NAME} operates globally and may transfer your data to servers located outside your country,
        including:
      </TextBlock>
      <List
        items={[
          'Firebase servers (Google Cloud) in the United States and other regions',
          'Azure servers in various global locations',
        ]}
      />
      <TextBlock>We ensure adequate protection through:</TextBlock>
      <List
        items={[
          'Standard Contractual Clauses (SCCs)',
          'Data Processing Agreements with providers',
          'Compliance with GDPR and other international standards',
        ]}
      />
 
      <SectionTitle>9. Cookies and Tracking Technologies</SectionTitle>
      <TextBlock>We use cookies and similar technologies to enhance your experience:</TextBlock>
 
      <SubSectionTitle>9.1 Types of Cookies</SubSectionTitle>
      <List
        items={[
          <><strong>Essential Cookies:</strong> Required for basic functionality (login, security)</>,
          <><strong>Analytics Cookies:</strong> Track usage patterns and performance</>,
          <><strong>Preference Cookies:</strong> Remember your settings and preferences</>,
          <><strong>Advertising Cookies:</strong> (If applicable) Show relevant ads</>,
        ]}
      />
 
      <SubSectionTitle>9.2 Your Choices</SubSectionTitle>
      <List
        items={[
          'Manage cookies in your browser settings',
          'Opt out of analytics in app settings',
          'Use browser extensions to block tracking',
        ]}
      />
      <TextBlock>
        <strong>Note:</strong> Disabling essential cookies may affect app functionality.
      </TextBlock>
 
      <SectionTitle>10. Third-Party Links</SectionTitle>
      <TextBlock>
        Our Service may contain links to third-party websites or services (e.g., social media, payment
        processors). We are not responsible for their privacy practices. Please review their privacy
        policies before providing any information.
      </TextBlock>
 
      <SectionTitle>11. Beta Testing and Feedback</SectionTitle>
      <TextBlock>{COMPANY_NAME} is currently in Beta. By participating:</TextBlock>
      <List
        items={[
          'You acknowledge the app is under development',
          'You may encounter bugs or incomplete features',
          'Feedback you provide may be used to improve the Service',
          'Your usage data helps us refine AI models and features',
        ]}
      />
 
      <SectionTitle>12. Changes to This Privacy Policy</SectionTitle>
      <TextBlock>We may update this Privacy Policy periodically to reflect:</TextBlock>
      <List
        items={[
          'Changes in our practices',
          'New features or services',
          'Legal or regulatory requirements',
        ]}
      />
      <TextBlock><strong>How We Notify You:</strong></TextBlock>
      <List
        items={[
          'Email notification to registered users',
          'In-app notification',
          'Updated "Last Modified" date at the top of this policy',
        ]}
      />
      <TextBlock>
        <strong>Your Continued Use:</strong> By continuing to use {COMPANY_NAME} after changes take effect,
        you accept the updated Privacy Policy.
      </TextBlock>
      <TextBlock>We encourage you to review this policy periodically.</TextBlock>
 
      <SectionTitle>13. California Privacy Rights (CCPA)</SectionTitle>
      <TextBlock>If you're a California resident, you have additional rights:</TextBlock>
      <List
        items={[
          <><strong>Right to Know:</strong> What personal information we collect, use, and share</>,
          <><strong>Right to Delete:</strong> Request deletion of your data (with exceptions)</>,
          <>
            <strong>Right to Opt-Out:</strong> Opt out of the "sale" of personal information (we don't sell
            data)
          </>,
          <>
            <strong>Right to Non-Discrimination:</strong> We won't discriminate against you for exercising
            your rights
          </>,
        ]}
      />
      <TextBlock>
        To exercise these rights, email {CONTACT_EMAIL} or call {CONTACT_PHONE}
      </TextBlock>
      <TextBlock><strong>Response Time:</strong> We will respond within 45 days.</TextBlock>
 
      <SectionTitle>14. Contact Us</SectionTitle>
      <TextBlock>
        If you have questions, concerns, or requests regarding this Privacy Policy or your data:
      </TextBlock>
      <TextBlock>
        <strong>{COMPANY_NAME} Support Team</strong><br />
        <strong>Email:</strong> {CONTACT_EMAIL}<br />
        <strong>Phone:</strong> {CONTACT_PHONE}<br />
        <strong>WhatsApp:</strong> {CONTACT_PHONE}
      </TextBlock>
      <TextBlock><strong>Social Media:</strong></TextBlock>
      <List
        items={[
          'Facebook: https://facebook.com/aastrog',
          'Instagram: https://instagram.com/aastrog',
          'Twitter/X: https://x.com/aastrog',
        ]}
      />
      <TextBlock><strong>Response Time:</strong> We aim to respond within 48-72 hours.</TextBlock>
 
      <SectionTitle>15. Disclaimer</SectionTitle>
      <TextBlock>
        <strong>Astrology is for Guidance Only:</strong><br />
        {COMPANY_NAME} provides astrological predictions, insights, and consultations for entertainment,
        self-reflection, and guidance purposes only. We do not guarantee outcomes or results.
      </TextBlock>
      <TextBlock>
        <strong>Not Professional Advice:</strong><br />
        Our Service does not replace professional medical, legal, financial, or psychological advice.
        Consult qualified professionals for serious matters.
      </TextBlock>
      <TextBlock>
        <strong>Your Responsibility:</strong><br />
        You are responsible for decisions made based on astrological guidance. {COMPANY_NAME} and its
        astrologers are not liable for any consequences.
      </TextBlock>
 
      <SectionTitle>16. Consent</SectionTitle>
      <TextBlock>By using {COMPANY_NAME}, you acknowledge that you have:</TextBlock>
      <List
        items={[
          'Read and understood this Privacy Policy',
          'Agreed to the collection, use, and sharing of your information as described',
          'Confirmed you are 18+ years old or have parental consent',
        ]}
      />
 
 
     
    </div>
  );
}
 
export default Policy;